// PoolDetailPage — /inbox/pool/:id
//
// Phase 4 sibling to LeadDetailPage, for pool quote requests.
//
// Differences from the direct-lead detail:
//   - No phone (customers don't share phone with the broadcast pool).
//   - No "Mark as booked" — pool requests go to many shops; the booking
//     itself is invisible to us. The artist can "respond" but never
//     definitively close the request.
//   - Shows requested styles, budget, and availability — the fields the
//     pool form collects beyond the direct-lead set.
//   - Shows a freshness line: "Posted 2 days ago, expires in 28 days".
//   - Auto-marks-as-viewed via savePoolView (idempotent).
//   - Private notes upsert into pool_request_views (creates a view row
//     if one doesn't exist yet).
//
// Status values for pool requests: viewed / responded / passed / archived.
// "Responded" is the equivalent of direct's "contacted". No "new" status
// stored — the absence of a pool_request_views row means "unread."

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useLeadsInbox } from "../hooks/useLeadsInbox";
import { useSubscription } from "../hooks/useSubscription";

// -- Same button-class workaround as InboxPage / LeadDetailPage. The rules
//    we're guarding against are `button { background: #111827 }` in
//    src/styles.css (no !important) and `.tattoo-app button { ... }` in
//    tattoo-ui.css (scoped to the editor, doesn't reach this page). Class
//    specificity already beats element specificity, so the !important
//    markers are technically redundant — kept defensively until a visual
//    sweep confirms removal.
const detailButtonStyles = `
.tt-pool-action-primary {
  background: #a2332b !important;
  color: #ffffff !important;
}
.tt-pool-action-primary:hover:not(:disabled) {
  background: #c2403a !important;
}
.tt-pool-action-ghost {
  background: transparent !important;
  color: #d7d7d7 !important;
}
.tt-pool-action-ghost:hover:not(:disabled) {
  background: #1a1a1a !important;
}
`;

const STATUS_PILL = {
  new: { label: "New", bg: "#a2332b", fg: "#fff" },
  viewed: { label: "Viewed", bg: "#2a2a2a", fg: "#cfcfcf" },
  responded: { label: "Responded", bg: "#1d4ed8", fg: "#fff" },
  passed: { label: "Passed", bg: "#3a3a3a", fg: "#a8a8a8" },
  archived: { label: "Archived", bg: "#3a3a3a", fg: "#a8a8a8" },
};

const NOTES_DEBOUNCE_MS = 800;

export default function PoolDetailPage() {
  const navigate = useNavigate();
  const { id: requestId } = useParams();
  const sub = useSubscription();
  const inbox = useLeadsInbox();

  const entry = useMemo(
    () => inbox.pool.find((p) => p.request.id === requestId) ?? null,
    [inbox.pool, requestId]
  );

  // Auto-mark-as-viewed on mount, exactly once per requestId. Idempotent
  // at the hook level (won't re-insert if a view row already exists).
  const viewedFiredFor = useRef(null);
  useEffect(() => {
    if (!entry) return;
    if (viewedFiredFor.current === entry.request.id) return;
    viewedFiredFor.current = entry.request.id;
    inbox.savePoolView(entry.request.id).catch((err) => {
      console.warn("[PoolDetailPage] savePoolView failed", err);
    });
  }, [entry, inbox]);

  // --- entitlement gate ---------------------------------------------------
  if (!sub.loading && !sub.isEntitled("leads")) {
    return (
      <PageShell onBack={() => navigate("/account/billing")}>
        <Card>
          <h3 style={emptyTitleStyle}>Inbox is an Artist Pro feature</h3>
          <p style={emptyBodyStyle}>
            Subscribe to Artist Pro (or start the trial) to access the inbox.
          </p>
        </Card>
      </PageShell>
    );
  }

  // --- loading ------------------------------------------------------------
  if (sub.loading || inbox.loading) {
    return (
      <PageShell onBack={() => navigate("/inbox")}>
        <p style={{ color: "#a8a8a8" }}>Loading pool request…</p>
      </PageShell>
    );
  }

  // --- inbox load error ---------------------------------------------------
  // The hook surfaces load failures on `inbox.error`. Show that BEFORE the
  // "not available" branch so we don't tell the user the request is gone
  // when the truth is "we couldn't reach the server".
  if (inbox.error) {
    return (
      <PageShell onBack={() => navigate("/inbox")}>
        <Card>
          <h3 style={emptyTitleStyle}>Couldn't load this request</h3>
          <p style={emptyBodyStyle}>{inbox.error}</p>
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              className="tt-pool-action-ghost"
              onClick={() => inbox.refresh()}
              style={ghostButtonStyle}
            >
              Try again
            </button>
          </div>
        </Card>
      </PageShell>
    );
  }

  // --- not found ----------------------------------------------------------
  if (!entry) {
    return (
      <PageShell onBack={() => navigate("/inbox")}>
        <Card>
          <h3 style={emptyTitleStyle}>Request not available</h3>
          <p style={emptyBodyStyle}>
            This pool request has expired, been removed, or you don't have
            access to it.
          </p>
        </Card>
      </PageShell>
    );
  }

  return (
    <PoolDetailContent
      entry={entry}
      shop={inbox.shop}
      onBack={() => navigate("/inbox")}
      savePoolStatus={inbox.savePoolStatus}
      savePoolNotes={inbox.savePoolNotes}
    />
  );
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

function PoolDetailContent({
  entry,
  shop,
  onBack,
  savePoolStatus,
  savePoolNotes,
}) {
  const { request, view } = entry;

  const initialNotes = view?.artist_notes ?? "";
  const [notes, setNotes] = useState(initialNotes);
  const lastSavedRef = useRef(initialNotes);
  const debounceRef = useRef(null);
  // Mirror LeadDetailPage: track whether the most recent save attempt
  // failed so the inline indicator can swap from "Saving…" to a clear
  // failure state. Cleared on the next typing event.
  const [saveFailed, setSaveFailed] = useState(false);

  const [busy, setBusy] = useState(null); // null | "reply" | "pass" | "archive"

  // If a different request arrives via in-page nav, sync the textarea.
  useEffect(() => {
    const incoming = view?.artist_notes ?? "";
    setNotes(incoming);
    lastSavedRef.current = incoming;
    setSaveFailed(false);
  }, [request.id, view?.artist_notes]);

  // Debounced auto-save for notes.
  useEffect(() => {
    if (notes === lastSavedRef.current) return;
    setSaveFailed(false);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await savePoolNotes(request.id, notes);
        lastSavedRef.current = notes;
        setSaveFailed(false);
      } catch {
        setSaveFailed(true);
        toast.error("Couldn't save notes — keep typing to retry.");
      }
    }, NOTES_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [notes, request.id, savePoolNotes]);

  const statusKey = view?.status || "new";
  const pill = STATUS_PILL[statusKey] || STATUS_PILL.viewed;
  const customerName = request.user_name?.trim() || "Anonymous";
  const shopName = shop?.display_name || shop?.name || "Tattune";

  const designImages = Array.isArray(request.design_images)
    ? request.design_images
    : [];
  const styles = Array.isArray(request.style_tags) ? request.style_tags : [];
  const location = [request.user_city, request.user_country]
    .filter(Boolean)
    .join(", ");

  // ---- actions -----------------------------------------------------------

  function handleReplyViaEmail() {
    if (!request.user_email) {
      toast.error("No email on file for this customer.");
      return;
    }
    const subject = `Re: Your Tattune quote request`;
    const greetingName =
      request.user_name?.trim()?.split(" ")?.[0] || "there";
    const body = [
      `Hi ${greetingName},`,
      ``,
      `[your reply here]`,
      ``,
      `— ${shopName}`,
      `Sent via Tattune`,
    ].join("\n");
    const mailto = `mailto:${encodeURIComponent(
      request.user_email
    )}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailto;

    // Stamp status as responded + responded_at, but only if currently
    // unset/viewed. Don't overwrite passed/archived from a Reply click.
    if (!view || view.status === "viewed") {
      setBusy("reply");
      savePoolStatus(request.id, "responded", { responded: true })
        .catch(() => {
          toast.error("Couldn't update status — refresh to retry.");
        })
        .finally(() => setBusy(null));
    }
  }

  async function handlePass() {
    setBusy("pass");
    try {
      await savePoolStatus(request.id, "passed");
      toast.success("Passed.");
    } catch {
      toast.error("Couldn't update — try again.");
    } finally {
      setBusy(null);
    }
  }

  async function handleArchive() {
    setBusy("archive");
    try {
      await savePoolStatus(request.id, "archived");
      toast.success("Archived.");
    } catch {
      toast.error("Couldn't update — try again.");
    } finally {
      setBusy(null);
    }
  }

  // ---- render ------------------------------------------------------------

  return (
    <PageShell onBack={onBack}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 600,
              margin: 0,
              letterSpacing: "0.01em",
            }}
          >
            {customerName}
          </h1>
          <div style={{ fontSize: 13, color: "#a8a8a8", marginTop: 4 }}>
            Posted {formatFullDate(request.created_at)}
          </div>
          {request.expires_at && (
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
              {formatFreshness(request.created_at, request.expires_at)}
            </div>
          )}
        </div>
        <StatusPill spec={pill} />
      </div>

      {/* Contact card */}
      <Card>
        <Row label="Email">
          {request.user_email ? (
            <a
              href={`mailto:${request.user_email}`}
              style={{ color: "#9ec5ff", textDecoration: "none" }}
            >
              {request.user_email}
            </a>
          ) : (
            <Muted>—</Muted>
          )}
        </Row>
        <Row label="Location">
          {location ? location : <Muted>—</Muted>}
        </Row>
      </Card>

      {/* Request details — styles / budget / availability */}
      <SectionHeader>Request details</SectionHeader>
      <Card>
        <Row label="Styles">
          {styles.length > 0 ? (
            <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>
              {styles.map((s) => (
                <Tag key={s}>{s}</Tag>
              ))}
            </span>
          ) : (
            <Muted>Not specified</Muted>
          )}
        </Row>
        <Row label="Budget">
          {request.budget_text ? request.budget_text : <Muted>Not specified</Muted>}
        </Row>
        <Row label="When">
          {request.availability_text ? (
            request.availability_text
          ) : (
            <Muted>Not specified</Muted>
          )}
        </Row>
      </Card>

      {/* Their note */}
      <SectionHeader>Their note</SectionHeader>
      <Card>
        <p
          style={{
            fontSize: 14,
            color: "#eaeaea",
            lineHeight: 1.55,
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {request.note?.trim() ? (
            <>“{request.note.trim()}”</>
          ) : (
            <Muted>No note left.</Muted>
          )}
        </p>
      </Card>

      {/* Design preview */}
      <SectionHeader>Design preview</SectionHeader>
      <Card>
        {designImages.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            {designImages.map((src, i) => (
              <a
                key={i}
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  width: 140,
                  height: 140,
                  borderRadius: 10,
                  background: "#1a1a1a",
                  border: "1px solid #2a2a2a",
                  overflow: "hidden",
                }}
              >
                <img
                  src={src}
                  alt={`Design ${i + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </a>
            ))}
          </div>
        ) : (
          <Muted>No images attached.</Muted>
        )}

        {request.placement_image && (
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                fontSize: 12,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 6,
              }}
            >
              Placement photo
            </div>
            <a
              href={request.placement_image}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                width: 200,
                height: 200,
                borderRadius: 10,
                background: "#1a1a1a",
                border: "1px solid #2a2a2a",
                overflow: "hidden",
              }}
            >
              <img
                src={request.placement_image}
                alt="Placement"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </a>
          </div>
        )}

        {request.share_url && (
          <div style={{ marginTop: 14 }}>
            <a
              href={request.share_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                border: "1px solid #2a2a2a",
                borderRadius: 10,
                color: "#d7d7d7",
                fontSize: 13,
                textDecoration: "none",
              }}
            >
              Open interactive 3D preview ↗
            </a>
          </div>
        )}
      </Card>

      {/* Private notes */}
      <SectionHeader>Your private notes</SectionHeader>
      <p
        style={{
          fontSize: 12,
          color: "#888",
          margin: "0 0 8px",
        }}
      >
        Only you can see these.
      </p>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="e.g. great match for me, mailed back with a quote and availability."
        rows={4}
        style={{
          width: "100%",
          padding: "12px 14px",
          background: "#0f0f0f",
          border: "1px solid #1f1f1f",
          borderRadius: 12,
          color: "#eaeaea",
          fontSize: 14,
          resize: "vertical",
          outline: "none",
          fontFamily: "inherit",
          lineHeight: 1.5,
          boxSizing: "border-box",
        }}
      />
      <div
        style={{
          fontSize: 11,
          color: saveFailed ? "#f6caca" : "#666",
          marginTop: 6,
          minHeight: 14,
        }}
      >
        {saveFailed
          ? "Save failed — keep typing to retry"
          : notes !== lastSavedRef.current
          ? "Saving…"
          : "Saved"}
      </div>

      {/* Actions — no booking for pool */}
      <SectionHeader>Actions</SectionHeader>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <button
          type="button"
          className="tt-pool-action-primary"
          onClick={handleReplyViaEmail}
          disabled={!!busy}
          style={primaryButtonStyle}
        >
          Reply via email
        </button>

        <button
          type="button"
          className="tt-pool-action-ghost"
          onClick={handlePass}
          disabled={!!busy || view?.status === "passed"}
          style={ghostButtonStyle}
        >
          {busy === "pass" ? "Passing…" : "Pass"}
        </button>

        <button
          type="button"
          className="tt-pool-action-ghost"
          onClick={handleArchive}
          disabled={!!busy || view?.status === "archived"}
          style={ghostButtonStyle}
        >
          {busy === "archive" ? "Archiving…" : "Archive"}
        </button>
      </div>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Layout + atoms (mirrors LeadDetailPage)
// ---------------------------------------------------------------------------

function PageShell({ children, onBack }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0a0a",
        color: "#eaeaea",
        padding: "32px 20px 80px",
      }}
    >
      <style>{detailButtonStyles}</style>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "transparent",
            border: "1px solid #2a2a2a",
            color: "#d7d7d7",
            padding: "6px 12px",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          ← Back to inbox
        </button>
        {children}
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div
      style={{
        background: "#111111",
        border: "1px solid #1f1f1f",
        borderRadius: 14,
        padding: "18px 20px",
      }}
    >
      {children}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <h3
      style={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "#888",
        margin: "24px 0 10px",
        fontWeight: 600,
      }}
    >
      {children}
    </h3>
  );
}

function Row({ label, children }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 12,
        padding: "6px 0",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#888",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          minWidth: 70,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          color: "#eaeaea",
          textAlign: "right",
          overflowWrap: "anywhere",
          flex: 1,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function StatusPill({ spec }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        background: spec.bg,
        color: spec.fg,
        whiteSpace: "nowrap",
      }}
    >
      {spec.label}
    </span>
  );
}

function Tag({ children }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        background: "#1a1a1a",
        border: "1px solid #2a2a2a",
        color: "#d7d7d7",
      }}
    >
      {children}
    </span>
  );
}

function Muted({ children }) {
  return <span style={{ color: "#666" }}>{children}</span>;
}

const primaryButtonStyle = {
  padding: "11px 18px",
  border: "none",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  letterSpacing: "0.02em",
};

const ghostButtonStyle = {
  padding: "10px 16px",
  border: "1px solid #2a2a2a",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const emptyTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  margin: "0 0 8px",
  color: "#eaeaea",
};

const emptyBodyStyle = {
  fontSize: 14,
  color: "#a8a8a8",
  lineHeight: 1.55,
  margin: 0,
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatFullDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatFreshness(createdIso, expiresIso) {
  if (!createdIso || !expiresIso) return "";
  const created = new Date(createdIso);
  const expires = new Date(expiresIso);
  if (Number.isNaN(created.getTime()) || Number.isNaN(expires.getTime())) {
    return "";
  }
  const now = new Date();

  const ageMs = now.getTime() - created.getTime();
  const ageDays = Math.max(0, Math.floor(ageMs / (1000 * 60 * 60 * 24)));
  const ageLabel =
    ageDays === 0 ? "Posted today" : ageDays === 1 ? "Posted 1 day ago" : `Posted ${ageDays} days ago`;

  const remainMs = expires.getTime() - now.getTime();
  let expiresLabel;
  if (remainMs <= 0) {
    expiresLabel = "expired";
  } else {
    const remainDays = Math.ceil(remainMs / (1000 * 60 * 60 * 24));
    expiresLabel =
      remainDays === 1 ? "expires tomorrow" : `expires in ${remainDays} days`;
  }

  return `${ageLabel}, ${expiresLabel}.`;
}
